'use client'

import type { MenuItem, MenuSeparator } from '@graftcode/design-system'
import { Avatar } from '@graftcode/design-system'

import { BASE_PROJECT_ITEMS, createSwitchProjectItem } from '@common/core/const'
import { getSafeImageUrl } from '@common/core/helpers'
import type { Project } from '@common/modules/projects/types/project.types'
import type { User } from '@common/modules/user/types/user.type'

function getInitials(name?: string): string {
  if (!name) return ''

  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length >= 2) {
    const firstChar = words[0]!.charAt(0)
    const lastChar = words[words.length - 1]!.charAt(0)
    return (firstChar + lastChar).toUpperCase()
  }

  return name.substring(0, 2).toUpperCase()
}

export function createProjectItems(projects: Project[], currentProject: Project | null): (MenuItem | MenuSeparator)[] {
  const projectsForPicker =
    currentProject && !projects.some((project) => project.id === currentProject.id)
      ? [...projects, currentProject]
      : projects

  const projectGroupItems = projectsForPicker.map((project) => {
    const isSelected = currentProject?.id === project.id
    const logoUrl = getSafeImageUrl(project.logo)
    const initials = getInitials(project?.name)

    return {
      type: 'item' as const,
      active: true,
      selected: isSelected,
      icon: logoUrl ? <Avatar size={20} src={logoUrl} /> : <Avatar size={20} fallback={initials} />,
      groups: [],
      label: project.name,
      id: `project-${project.id}`,
      size: 'xs' as const,
    }
  })

  return [...BASE_PROJECT_ITEMS, createSwitchProjectItem(projectGroupItems)]
}

export function createProfileItems(
  baseItems: (MenuItem | MenuSeparator)[],
  currentUser: Partial<User> | User | null
): (MenuItem | MenuSeparator)[] {
  if (!currentUser) {
    return baseItems.slice(1)
  }

  if (!baseItems.length || baseItems[0]?.type !== 'item') {
    return baseItems
  }

  const firstItem = baseItems[0] as MenuItem
  const regionalData = currentUser?.regionalData
  const email = regionalData?.email || currentUser?.profile?.email || ''
  const firstName = regionalData?.firstname || ''
  const lastName = regionalData?.lastname || ''

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || firstItem.label
  const displayEmail = email || firstItem.subLabel

  const updatedFirstItem: MenuItem = {
    ...firstItem,
    label: fullName,
    subLabel: displayEmail,
  }

  return [updatedFirstItem, ...baseItems.slice(1)]
}
